import { Component } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { GraphqlService } from 'src/app/graphql/graphql.service';
import * as models from 'src/app/shared/models';
import { AbstractPageForm } from '../../pages/page-form.abstract';

@Component({
    selector: 'pk-web-newsletter-modal',
    templateUrl: './newsletter-modal.component.html',
    styleUrl: './newsletter-modal.component.scss',
    standalone: false
})
export class NewsletterModalComponent extends AbstractPageForm {
	public showSuccessMessage = false;
	public defaultCountries: models.Country[];
	public countries: models.Country[];

	public phoneMasksMap: { [key: number]: (string | RegExp)[] } = {
		[this.CONSTANTS.countries.unitedStates]: this.CONSTANTS.masks.phoneUS,
		[this.CONSTANTS.countries.canada]: this.CONSTANTS.masks.phoneUS,
		[this.CONSTANTS.countries.unitedKingdom]: this.CONSTANTS.masks.phoneGB,
		[this.CONSTANTS.countries.ireland]: this.CONSTANTS.masks.phoneIE,
		[this.CONSTANTS.countries.singapore]: this.CONSTANTS.masks.phoneSG,
		[this.CONSTANTS.countries.australia]: this.CONSTANTS.masks.phoneAU,
		[this.CONSTANTS.countries.france]: this.CONSTANTS.masks.phoneFR,
	};

	private defaultCrmAgent: models.Agent;

	constructor(
		private activeModal: NgbActiveModal,
		private graphqlService: GraphqlService,
		private fb: FormBuilder,
	) {
		super();
		this.setSubmitText('Submit', 'Submitting');
	}

	public override async loadPageData(): Promise<void> {
		const result = await this.graphqlService.query<{
			countries: models.PagedData<models.Country>;
			agents: models.PagedData<models.Agent>;
		}>(`
				query GetContactUsPageData {
					countries(criteria: { isActive: true }, sort: "name") {
						message {
							id
							name
							code
						}
					}
					agents(criteria: { isInHouseIndirectSale: true, isActive: true }) {
						message {
							agentId
							parentId
						}
					}
				}
			`);

		const usIndex = result.data.countries.message.findIndex(c => c.id === this.CONSTANTS.countries.unitedStates);
		const ukIndex = result.data.countries.message.findIndex(c => c.id === this.CONSTANTS.countries.unitedKingdom);
		const US = result.data.countries.message[usIndex];
		const UK = result.data.countries.message[ukIndex];
		this.defaultCountries = [US, UK];
		this.countries = result.data.countries.message.filter(
			c => ![this.CONSTANTS.countries.unitedStates, this.CONSTANTS.countries.unitedKingdom].includes(c.id),
		);
		this.defaultCrmAgent = result.data.agents.message.find(a => !a.parentId);
	}

	public override getForm() {
		return this.fb.group({
			firstName: ['', [Validators.required]],
			lastName: ['', [Validators.required]],
			companyName: '',
			email: ['', [Validators.required, Validators.email]],
			phone: '',
			country: [this.countries.concat(this.defaultCountries).find(c => c.id === this.CONSTANTS.countries.unitedStates), [Validators.required]],
		});
	}

	public override async onFormLoaded(): Promise<void> {
		this.formGroup.valueChanges.subscribe(() => {
			this.showSuccessMessage = false;
		});
	}

	public chooseCountry(country: models.Country): void {
		this.form.country.setValue(country);
		this.form.phone.setValue('');
	}

	public override async onFormSubmitted(): Promise<void> {
		// normally page-abstract.ts will catch this error but to enhance the user
		// experience we want to succeed for them every time so we'll wrap in this
		// try/catch here.  Our logs will pick up any issue and we can silently
		// fix any issues while still capturing the user's contact details
		try {
			this.form.country.disable();
			const lead = this.getLeadDto();
			await this.graphqlService.mutate<{ createLead: models.Lead }>(
				`
				mutation NewsletterModalAddLead($lead: LeadInput!) {
					createLead(lead: $lead) {
						id
					}
				}
			`,
				{ lead },
			);
		} catch (e) {
			// do nothing
		}

		this.form.country.enable();
		this.reloadForm();
		this.showSuccessMessage = true;
	}

	private getLeadDto(): Partial<models.Lead> {
		return {
			tierLevel: 3,
			leadStatusId: this.CONSTANTS.leadStatusesBroker.newLeads,
			dbaName: this.form.companyName.value,
			leadCategoryId: 1, // Direct
			leadContacts: [
				{
					isPrimary: true,
					fName: this.form.firstName.value,
					lName: this.form.lastName.value,
					mobile: this.form.phone.value,
					mobileCountryId: this.form.country.value.id,
					phone: this.form.phone.value,
					phoneCountryId: this.form.country.value.id,
					email: this.form.email.value,
				},
				{
					isPrimary: false,
				},
				{
					isPrimary: false,
				},
			] as any[],
			leadTypeId: this.CONSTANTS.leadTypes.broker,
			agentId: this.defaultCrmAgent.agentId,
		};
	}

	public close(): void {
		this.activeModal.dismiss('Close Click');
	}
}
